import { Injectable } from '@angular/core';
import { isReturnableUrl } from './returnable-url';

/**
 * Carries the page a user was trying to reach across the login round-trip, so a
 * shared deep link (`/quotations/566?page=1&count=25`) still lands on the order
 * after an expired session forces a re-authentication.
 *
 * Two paths capture: `AuthGuard`, when there is no token at all, and
 * `SessionService.expireSession()`, when a token exists but the refresh fails.
 * An explicit Logout clears instead — signing back in as someone else should
 * start from the orders list, not from the previous user's last screen.
 *
 * Stored in `sessionStorage`, deliberately: `clearStorageExceptPreserved()`
 * wipes `localStorage` on every logout path, which is exactly the moment this
 * value has to survive. Scoping it to the tab is also the correct lifetime — the
 * intent belongs to this navigation, not to the browser profile.
 */
@Injectable({ providedIn: 'root' })
export class AuthReturnUrlService {
  private static readonly STORAGE_KEY = 'wolsal.auth.returnUrl';

  /**
   * Remember `url` as the post-login destination, if it is somewhere we can
   * safely send a user. Unsafe or empty URLs clear any previous value rather
   * than leaving a stale one behind to fire on the next login.
   */
  capture(url: string | null | undefined): void {
    if (!isReturnableUrl(url)) {
      this.clear();
      return;
    }

    try {
      sessionStorage.setItem(AuthReturnUrlService.STORAGE_KEY, url as string);
    } catch {
      // Private browsing or a full quota — the redirect is a convenience, so
      // falling back to the default destination is an acceptable outcome.
    }
  }

  /**
   * Read and remove the stored destination. Single-use by design: a redirect
   * that survived its own login would hijack every later visit to the login
   * page in this tab.
   */
  consume(): string | null {
    let stored: string | null = null;

    try {
      stored = sessionStorage.getItem(AuthReturnUrlService.STORAGE_KEY);
    } catch {
      return null;
    }

    this.clear();

    // Re-validated on the way out: storage is writable by anything running in
    // the tab, so trusting the value we wrote is not the same as trusting the
    // value we read.
    return isReturnableUrl(stored) ? stored : null;
  }

  clear(): void {
    try {
      sessionStorage.removeItem(AuthReturnUrlService.STORAGE_KEY);
    } catch {
      // Nothing to recover from — see capture().
    }
  }
}
